import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ProjectReportGenerationDataGridService } from '../project-report-generation/project-report-generation-data-grid.service';
import { GridCellRendererComponent } from 'libs/ui/src/lib/grid-cell-renderer/grid-cell-renderer.component';
import { InlineActionButtonComponent } from 'libs/ui/src/lib/inline-action-button/inline-action-button.component';
import { NewProjectInlineDropdownComponent } from 'libs/ui/src/lib/new-project-inline-dropdown/new-project-inline-dropdown.component';
import { ReportsService } from 'libs/data/src/lib/services/report/reports.service';
import {
  FeatureValidation,
  Helper,
  ProjectsFacade,
  addFullStop,
  customDecode,
  customEncode,
  decrypt,
  getRandomColorWithOpacity,
  isFeatureEnabled,
  showFeatureAlert,
  validateFeatures,
} from '@piqet-angular/data';
import { Subject, Subscription, debounceTime, distinctUntilChanged, lastValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { DataQualityAssessmentDropdownComponent } from 'libs/ui/src/lib/data-quality-assessment-dropdown/data-quality-assessment-dropdown.component';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import {
  AccessPermissionService,
  circleColors,
  commonEnum,
  reviewColorList,
  FeatureCodes,
} from '@piqet-angular/shared';
import { ViewportScroller } from '@angular/common';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { JoyrideService } from 'ngx-joyride';
import { FileUploadConfig, FileUploadResult, FileUploadComponent } from '@piqet-angular/shared';

interface PartialUpdateModel {
  projectId: number;
  userId?: number;
  organizationId?: number;
  // Only include fields that have actually changed
  changedFields: {
    [key: string]: any;
  };
}

@Component({
  selector: 'piqet-angular-report-interpretation',
  templateUrl: './report-interpretation.component.html',
  styleUrls: ['./report-interpretation.component.scss'],
})
export class ReportInterpretationComponent implements OnDestroy {
  @ViewChild('systemBoundaryUpload', { static: false }) systemBoundaryUpload!: FileUploadComponent;

  FeatureCodes = FeatureCodes;
  reportChartForm!: FormGroup;

  // UI State
  isShowChange = false;
  selectedFileName: any;
  isImageValid = true;
  isImageSizeExceeds = false;
  showEdit: boolean = false;

  // Data
  imageUrl: any;
  fileBase64: any;
  reviewColorList = reviewColorList;
  circleColors = circleColors;

  systemBoundaryUploadConfig: Partial<FileUploadConfig> = {
    maxFileSize: 2,
    showPreview: false,
    placeholderText: 'Upload system boundary',
    showValidationError: true,
    customErrorMessage:
      'Only .jpg, .png, .jpeg, .gif files allowed. Size should be between 20KB to 2MB',
  };

  // Lists
  lstReasonForStudy: any = [];
  lstReviewer: any = [];
  systemBoundaryExamples: any = [];
  authorList: any[] = [];
  reviewerOrganizationList: any[] = [];

  // Grid
  ISOColumnDef: any;
  dataQualityAssessmnetList: any = [];

  // IDs and metadata
  activatedRouteProjectId: any;
  currentProjectId: any;
  mTr: any;

  // Author search
  authorMetaData: any;
  authorPageNumber = 1;
  authorPageSize = 20;
  authorSearchTerm = '';
  isLoadingAuthors = false;
  allAuthorsLoaded = false;
  public searchSubject = new Subject<string>();

  // Subscriptions
  inlineGridData!: Subscription;
  public searchSubscription?: Subscription;

  // Framework components
  frameworkComponents = {
    inlineActionButtonComponent: InlineActionButtonComponent,
    DataQualityAssessmentDropdownComponent: DataQualityAssessmentDropdownComponent,
  };

  // Track original values for change detection
  private originalFormValues: any = {};
  private autoSaveTimeout: any;
  interpretationData: any;

  constructor(
    private formBuilder: FormBuilder,
    private projectReportGenerationDataGridService: ProjectReportGenerationDataGridService,
    private reportService: ReportsService,
    private toastrService: ToastrService,
    public projectFacade: ProjectsFacade,
    private accessService: AccessPermissionService,
    private router: Router,
    private sharedService: SharedService,
    public route: ActivatedRoute,
    private viewportScroller: ViewportScroller,
    private swalService: SwalService,
    private JoyrideService: JoyrideService
  ) {
    this.initializeRouteParams();
    this.setupAuthorSearch();
  }

  ngOnDestroy(): void {
    this.inlineGridData?.unsubscribe();
    this.searchSubscription?.unsubscribe();
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }
  }

  ngOnInit() {
    this.showEdit = this.accessService.hasPermission('edit', '', 'packaging');
    this.currentProjectId = Number(
      atob(decodeURIComponent(customDecode(location.href.split('/')[6])))
    );

    this.initializeForm();
    this.setupGridSubscription();
    this.asyncInitialFormData();

    !this.JoyrideService.isTourInProgress() && this.viewportScroller.scrollToPosition([0, 0]);
  }

  private initializeRouteParams() {
    const url = location.href.split('/');
    this.activatedRouteProjectId = Number(atob(decodeURIComponent(customDecode(url[6]))));

    this.route.queryParams.subscribe((values: any) => {
      let value: any = decrypt(values.param ? values.param : '');
      value = value && !value.includes('undefined') ? JSON.parse(value) : '';
      this.mTr = value['mTr'];
    });
  }

  private setupAuthorSearch() {
    this.searchSubscription = this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(searchTerm => {
        this.authorSearchTerm = searchTerm;
        this.getAuthors(true);
      });
  }

  private initializeForm() {
    this.reportChartForm = this.formBuilder.group({
      criticalViewer: [null],
      // organizationISO: [null],
      viewSysBoundry: [null],
      impactCategoryJustification: [''],
      otherAssumption: [''],
      goalAndScope: [''],
      introductionToISOReport: [''],
      ISOReportForm: this.formBuilder.group({
        reasonOfStudy: [null],
        criticalViewer: [null],
        // organizationISO: [{ value: null, disabled: true }],
        viewSysBoundry: [null],
        boundryJustification: [''],
        excludedProcessJusti: [''],
        multiFunJustification: [''],
        sensitivityAnalysisDescription: [''],
        geographicalBoundary: [''],
        temporalBoundary: [''],
        biogenicCarbonAccounting: [''],
        uncertaintyAnalysis: [''],
        conclusion: [''],
        interpretationText: [''],
        authors: [null],
      }),
    });

    // Store initial values for change detection
    this.storeOriginalValues();
  }

  private setupGridSubscription() {
    this.ISOColumnDef =
      this.projectReportGenerationDataGridService.getDataQualityAssessmentListGrid();
    this.inlineGridData =
      this.projectReportGenerationDataGridService.inlineEditingSaveData$.subscribe((data: any) => {
        data = { ...data, projectId: this.activatedRouteProjectId };
        this.saveInlineGrid(data);
      });
  }

  private storeOriginalValues() {
    this.originalFormValues = JSON.parse(JSON.stringify(this.reportChartForm.value));
  }

  private getChangedFields(): any {
    const currentValues = this.reportChartForm.value;
    const changedFields: any = {};

    // Compare top-level fields
    Object.keys(currentValues).forEach(key => {
      if (key === 'ISOReportForm') {
        // Handle nested form group
        const currentNested = currentValues[key];
        const originalNested = this.originalFormValues[key] || {};

        Object.keys(currentNested).forEach(nestedKey => {
          if (
            JSON.stringify(currentNested[nestedKey]) !== JSON.stringify(originalNested[nestedKey])
          ) {
            changedFields[nestedKey] = currentNested[nestedKey];
          }
        });
      } else {
        if (JSON.stringify(currentValues[key]) !== JSON.stringify(this.originalFormValues[key])) {
          changedFields[key] = currentValues[key];
        }
      }
    });

    // Add image changes if any
    if (this.fileBase64) {
      changedFields.systemBoundaryImage = this.fileBase64;
      changedFields.systemBoundaryImageName = this.selectedFileName;
    } else if (this.imageUrl === null) {
      changedFields.systemBoundaryImage = null;
      changedFields.systemBoundaryImageName = null;
    }

    return changedFields;
  }

  saveInlineGrid(data: any) {
    this.reportService.addUpdateDataQualityAssessment(data).subscribe((res: any) => {
      !res.messageType
        ? this.toastrService.success(res.message)
        : this.toastrService.error(res.message);
    });
  }

  async asyncInitialFormData() {
    await this.getReportInterpretationData();
  }

  async getReportInterpretationData() {
    try {
      this.interpretationData = await lastValueFrom(
        this.reportService.getInterpretationDetail(this.currentProjectId)
      );

      this.systemBoundaryExamples = this.interpretationData?.data?.systemBoundaryImageOptions;
      this.patchISOformData(this.interpretationData);
    } catch (error) {
      this.toastrService.error('Failed to load interpretation data');
      console.error('Error loading interpretation data:', error);
    }
  }

  patchISOformData(data: any) {
    // Set lookup data
    this.lstReasonForStudy = data?.data?.lstReasonForStudy || [];
    this.lstReviewer = data?.data?.lstReviewer || [];
    this.authorList = data?.data?.authorData?.data || [];
    this.authorMetaData = data?.data?.authorData?.metadata;
    // this.reviewerOrganizationList = data?.data?.reviewerOrganizationList || [];

    // Handle author pagination
    if (!this.authorMetaData?.hasMorePages) {
      this.allAuthorsLoaded = true;
    } else {
      this.authorPageNumber++;
    }

    this.dataQualityAssessmnetList =
      data?.data?.dataQualityAssessmentItemList?.dataQualityAssessmentData || [];

    // Patch form values
    const formValues = {
      impactCategoryJustification: data?.data?.impactCategoryJustification || '',
      otherAssumption: data?.data?.otherAssumptions || '',
      goalAndScope: data?.data?.goalAndScope || '',
      introductionToISOReport: data?.data?.introductionToISOReport || '',
    };

    const nestedFormValues = {
      reasonOfStudy: data?.data?.reasonForStudy ? parseFloat(data.data.reasonForStudy) : null,
      criticalViewer: this.parseCommaSeparatedIds(data?.data?.reviewer),
      authors: this.parseCommaSeparatedIds(data?.data?.authors),
      viewSysBoundry: data?.data?.systemBoundaryExamples || null,
      boundryJustification: data?.data?.systemBoundaryJustification || '',
      excludedProcessJusti: data?.data?.excludedProcessesJustification || '',
      multiFunJustification: data?.data?.multiFunctionalityJustification || '',
      conclusion: data?.data?.recommendationsAndConclusions || '',
      sensitivityAnalysisDescription: data?.data?.sensitivityAnalysisDescription || '',
      geographicalBoundary: data?.data?.geographicalBoundary || '',
      temporalBoundary: data?.data?.temporalBoundary || '',
      biogenicCarbonAccounting: data?.data?.approachToBiogenicCarbonDesc || '',
      uncertaintyAnalysis: data?.data?.uncertaintyAnalysisDescription || '',
      interpretationText: data?.data?.interpretation || '',
      // organizationISO: this.getOrganizationName(data?.data?.reviewer),
    };

    this.reportChartForm.patchValue(formValues);
    this.reportChartForm.controls['ISOReportForm'].patchValue(nestedFormValues);

    // Handle system boundary image
    if (data?.data?.systemBoundaryImage) {
      this.isShowChange = true;
      this.selectedFileName = data.data.systemBoundaryImage;
      // Set the image URL for existing file display
      // If there's a specific URL property, use it, otherwise construct or use the filename
      this.imageUrl =
        data?.data?.systemBoundaryImageUrl ||
        data?.data?.systemBoundaryImagePath ||
        data?.data?.systemBoundaryImage ||
        null;
    }

    this.getMaterialDataForGrid();
    this.storeOriginalValues(); // Store values after patching
  }

  private parseCommaSeparatedIds(value: string): number[] {
    return value ? value.split(',').map((id: string) => parseInt(id.trim(), 10)) : [];
  }

  // private getOrganizationName(reviewerIds: string): string {
  //   if (!reviewerIds || !this.lstReviewer || !this.reviewerOrganizationList) {
  //     return '';
  //   }

  //   const selectedReviewerIds = this.parseCommaSeparatedIds(reviewerIds);
  //   const selectedReviewerIdLongs = this.lstReviewer
  //     .filter((reviewer: any) => selectedReviewerIds.includes(reviewer.idLong))
  //     .map((reviewer: any) => reviewer.id);

  //   const orgList = Array.isArray(this.reviewerOrganizationList)
  //     ? this.reviewerOrganizationList
  //     : [this.reviewerOrganizationList];

  //   const matchedOrgs = orgList
  //     .filter((org: any) => selectedReviewerIdLongs.includes(org.idLong))
  //     .map((org: any) => org.name);

  //   return [...new Set(matchedOrgs)].join(', ');
  // }

  getMaterialDataForGrid() {
    this.ISOColumnDef =
      this.projectReportGenerationDataGridService.getDataQualityAssessmentListGrid();
    if (this.dataQualityAssessmnetList?.length) {
      this.ISOColumnDef[5].cellEditorParams['technologyList'] =
        this.interpretationData.data.dataQualityAssessmentItemList.dataQualityAssessmentTechnologyData;
    }
    this.projectFacade.getDataAnalysisData(this.dataQualityAssessmnetList);
  }

  // File handling methods
  onSystemBoundaryFileSelected(result: FileUploadResult): void {
    if (!result.isValid || !result.file) return;

    // Validate file size (20KB to 2MB)
    const fileSizeKB = result.file.size / 1024;
    if (fileSizeKB < 20 || fileSizeKB > 2048) {
      this.isImageSizeExceeds = true;
      this.isImageValid = false;
      return;
    }

    this.isImageSizeExceeds = false;
    this.isImageValid = true;
    this.selectedFileName = result.fileName;
    this.fileBase64 = result.fileBase64;
    this.imageUrl = result.fileUrl;
    this.isShowChange = true;

    this.debouncedAutoSave();
  }

  onSystemBoundaryFileRemoved(): void {
    // Reset local state first
    this.isShowChange = false;
    this.selectedFileName = null;
    this.imageUrl = null;
    this.isImageValid = true;
    this.isImageSizeExceeds = false;
    this.fileBase64 = null;

    // Reset the file upload component
    if (this.systemBoundaryUpload) {
      this.systemBoundaryUpload.reset();
    }

    this.debouncedAutoSave();
  }

  // Search methods
  commonInputSearchName(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  commonInputSearchDes(term: string, item: any) {
    return item.description.toLowerCase().includes(term.toLowerCase());
  }

  // Navigation methods
  nextPage(data?: any) {
    const encodedValue = encodeURIComponent(this.activatedRouteProjectId);
    this.router.navigate(
      [`projects/projects-setting/${customEncode(btoa(encodedValue))}/report-generation`],
      { queryParams: { param: customEncode(btoa(encodedValue)) } }
    );
    this.sharedService.accessSideBarData(() => {});
    this.sharedService.scenarioNameFunc('Report generation');

    if (this.showEdit) {
      this.saveChanges();
    }
  }

  onNext(step: string) {
    if (step == 'adv-13') {
      this.router.navigate(
        [
          `projects/projects-setting/${customEncode(
            btoa(encodeURIComponent(this.activatedRouteProjectId))
          )}/report-generation`,
        ],
        { queryParams: { mTr: this.mTr ? this.mTr : null } }
      );
      this.sharedService.accessSideBarData(() => {});
      localStorage.setItem('scenarioName', 'Report generation');
    }
  }

  // Save methods
  private validateFeatures(changedFields: any): string[] {
    const hasISOReportData = Object.keys(changedFields).length > 0;

    const validations: FeatureValidation[] = [
      {
        condition: hasISOReportData,
        featureCode: FeatureCodes.REPORTS_INTERPRETATION_SECTION,
        featureName: 'ISO Report - Interpretation Section',
      },
    ];

    return validateFeatures(validations, isFeatureEnabled);
  }

  saveChanges() {
    const changedFields = this.getChangedFields();

    if (Object.keys(changedFields).length === 0) {
      return; // No changes to save
    }

    const missingFeatures = this.validateFeatures(changedFields);
    if (missingFeatures.length > 0) {
      showFeatureAlert(missingFeatures, this.swalService);
      return;
    }

    const partialModel: PartialUpdateModel = {
      projectId: Number(this.currentProjectId),
      changedFields: this.processChangedFields(changedFields),
    };

    this.reportService.updateInterpretationDetailPartial(partialModel).subscribe({
      next: (res: any) => {
        if (!res.messageType) {
          this.toastrService.success(res.message);
          this.storeOriginalValues();
        } else {
          this.toastrService.error(res.message);
        }
      },
      error: error => {
        this.toastrService.error('Failed to save changes');
        console.error('Save error:', error);
      },
    });
  }

  private processChangedFields(changedFields: any): any {
    const processed: any = {};

    Object.keys(changedFields).forEach(key => {
      const value = changedFields[key];

      // Add full stops to text fields
      const textFields = [
        'goalAndScope',
        'impactCategoryJustification',
        'otherAssumption',
        'boundryJustification',
        'excludedProcessJusti',
        'multiFunJustification',
        'conclusion',
        'sensitivityAnalysisDescription',
        'geographicalBoundary',
        'temporalBoundary',
        'biogenicCarbonAccounting',
        'uncertaintyAnalysis',
        'interpretationText',
        'introductionToISOReport',
      ];

      if (textFields.includes(key) && typeof value === 'string') {
        processed[key] = addFullStop(value);
      } else if (key === 'reasonOfStudy' && value !== null) {
        processed[key] = JSON.stringify(value);
      } else if ((key === 'criticalViewer' || key === 'authors') && Array.isArray(value)) {
        processed[key] = value.join(', ');
      } else {
        processed[key] = value;
      }
    });

    return processed;
  }

  private debouncedAutoSave() {
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }

    this.autoSaveTimeout = setTimeout(() => {
      this.saveChanges();
    }, 200);
  }

  autoSave(gridData?: any) {
    this.debouncedAutoSave();
  }

  onSave(gridData: any) {
    this.saveChanges();
  }

  // Author search methods
  onAuthorSearch(event: any) {
    const term = typeof event === 'string' ? event : event?.term || '';
    this.searchSubject.next(term.trim());
  }

  onAuthorScrollToEnd() {
    if (!this.isLoadingAuthors && !this.allAuthorsLoaded) {
      this.getAuthors(false);
    }
  }

  onAuthorSearchInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const term = target?.value || '';
    this.searchSubject.next(term.trim());
  }

  getAuthors(reset: boolean = false) {
    if (this.isLoadingAuthors) return;

    this.isLoadingAuthors = true;

    if (reset) {
      this.authorList = [];
      this.authorPageNumber = 1;
      this.allAuthorsLoaded = false;
    }

    const request = {
      searchTerm: this.authorSearchTerm?.trim() || null,
      pageNumber: this.authorPageNumber,
      pageSize: this.authorPageSize,
      activeOnly: false,
    };

    this.reportService.searchMembers(request).subscribe({
      next: (res: any) => {
        const authors = res.data?.data || [];

        if (reset) {
          this.authorList = authors;
        } else {
          const existingIds = new Set(this.authorList.map(author => author.userId));
          const newAuthors = authors.filter((author: any) => !existingIds.has(author.userId));
          this.authorList = [...this.authorList, ...newAuthors];
        }

        const hasMorePages = res.data?.metadata?.hasMorePages ?? false;
        if (!hasMorePages || authors.length < this.authorPageSize) {
          this.allAuthorsLoaded = true;
        } else {
          this.authorPageNumber++;
        }

        this.isLoadingAuthors = false;
      },
      error: error => {
        console.error('Error loading authors:', error);
        this.isLoadingAuthors = false;
        this.toastrService.error('Failed to load authors');
      },
    });
  }

  clearAuthorSearch() {
    this.authorSearchTerm = '';
    this.getAuthors(true);
  }

  async downloadPreviewImage(event: MouseEvent, item: any) {
    event.preventDefault();
    event.stopPropagation();

    try {
      const response = await fetch(item.image);
      if (!response.ok) {
        console.error('Failed to fetch image');
        return;
      }

      const imageData = await response.blob();
      const blobUrl = URL.createObjectURL(imageData);
      const imageName = item.name.replace(/\./g, '');

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = imageName;
      a.target = '_self';
      document.body.appendChild(a);
      a.click();

      URL.revokeObjectURL(blobUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading image:', error);
      this.toastrService.error('Failed to download image');
    }
  }

  triggerScroll(): void {
    this.sharedService.triggerScroll();
  }
}
